<template>
  <!-- Desktop Navigation -->
  <header class="max-md:hidden bg-gray-900/50 backdrop-blur-md border-b border-gray-800 sticky top-0 z-50">
    <div class="container mx-auto px-4 h-16 flex items-center justify-between">
      <NuxtLink to="/" class="text-xl font-black bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent hover:opacity-80 transition-opacity">
        Crow Gifts
      </NuxtLink>
      <nav class="flex items-center gap-1 text-sm font-medium text-gray-400">
        <NuxtLink
          to="/"
          class="px-3 py-2 rounded-lg hover:text-white hover:bg-white/5 transition-colors"
          active-class="text-white bg-white/10"
        >Home</NuxtLink>
        
        <template v-if="loggedIn">
          <span class="text-gray-700">/</span>
          <NuxtLink
            to="/crows"
            class="px-3 py-2 rounded-lg hover:text-white hover:bg-white/5 transition-colors"
            active-class="text-white bg-white/10"
          >Crows</NuxtLink>
          <span class="text-gray-700">/</span>
          <NuxtLink
            to="/upload"
            class="px-3 py-2 rounded-lg hover:text-white hover:bg-white/5 transition-colors"
            active-class="text-white bg-white/10"
          >Upload</NuxtLink>
          <span class="text-gray-700">/</span>
          <button 
            @click.prevent="logout" 
            class="px-3 py-2 rounded-lg hover:text-red-400 hover:bg-red-400/10 transition-colors ml-2 cursor-pointer"
          >
            Logout
          </button>
        </template>
        <template v-else>
          <span class="text-gray-700">/</span>
          <NuxtLink 
            to="/auth/login" 
            class="px-3 py-2 rounded-lg hover:text-white hover:bg-white/5 transition-colors"
            active-class="text-white bg-white/10"
          >Login</NuxtLink>
        </template>
      </nav>
    </div>
  </header>

  <!-- Mobile Header (simplified) -->
  <header class="md:hidden bg-gray-900/50 backdrop-blur-md border-b border-gray-800 sticky top-0 z-50">
    <div class="px-4 h-14 flex items-center justify-center">
      <NuxtLink to="/" class="text-lg font-black bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
        Crow Gifts
      </NuxtLink>
    </div>
  </header>

  <!-- Mobile Bottom Navigation -->
  <nav v-if="loggedIn" class="md:hidden fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-lg border-t border-gray-800 z-50 safe-area-inset-bottom">
    <div class="grid grid-cols-4 h-16">
      <NuxtLink
        to="/"
        class="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors"
        :class="route.path === '/' ? 'text-purple-400' : 'hover:text-gray-300'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" :class="route.path === '/' ? 'fill-purple-400' : 'fill-current'" viewBox="0 0 20 20">
          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
        </svg>
        <span class="text-xs font-medium">Home</span>
      </NuxtLink>

      <NuxtLink
        to="/crows"
        class="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors"
        :class="route.path.startsWith('/crows') ? 'text-purple-400' : 'hover:text-gray-300'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" :class="route.path.startsWith('/crows') ? 'fill-purple-400' : 'fill-current'" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
        </svg>
        <span class="text-xs font-medium">Crows</span>
      </NuxtLink>

      <NuxtLink
        to="/upload"
        class="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors"
        :class="route.path === '/upload' ? 'text-purple-400' : 'hover:text-gray-300'"
      >
        <div class="relative">
          <div class="absolute -inset-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl blur opacity-30"></div>
          <div class="relative bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 fill-white" viewBox="0 0 20 20">
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
            </svg>
          </div>
        </div>
        <span class="text-xs font-medium">Upload</span>
      </NuxtLink>

      <button
        @click.prevent="logout"
        class="flex flex-col items-center justify-center gap-1 text-gray-500 hover:text-red-400 transition-colors cursor-pointer"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 fill-current" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clip-rule="evenodd" />
        </svg>
        <span class="text-xs font-medium">Logout</span>
      </button>
    </div>
  </nav>

  <!-- Mobile Bottom Navigation (Not Logged In) -->
  <nav v-else class="md:hidden fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-lg border-t border-gray-800 z-50 safe-area-inset-bottom">
    <div class="grid grid-cols-2 h-16">
      <NuxtLink
        to="/"
        class="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors"
        :class="route.path === '/' ? 'text-purple-400' : 'hover:text-gray-300'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" :class="route.path === '/' ? 'fill-purple-400' : 'fill-current'" viewBox="0 0 20 20">
          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
        </svg>
        <span class="text-xs font-medium">Home</span>
      </NuxtLink>

      <NuxtLink
        to="/auth/login"
        class="flex flex-col items-center justify-center gap-1 text-gray-500 transition-colors"
        :class="route.path === '/auth/login' ? 'text-purple-400' : 'hover:text-gray-300'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" :class="route.path === '/auth/login' ? 'fill-purple-400' : 'fill-current'" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
        <span class="text-xs font-medium">Login</span>
      </NuxtLink>
    </div>
  </nav>
</template>

<script setup>
import { useRoute } from 'vue-router'
const route = useRoute()
const { loggedIn, user, clear } = useUserSession()

const logout = async () => {
  await clear()
  return navigateTo('/auth/login')
}
</script>
